import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Save } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Loader } from "@/components/Loader";

const COLS = [["lp_view", "Visite"], ["lp_cta", "Clic"], ["quiz_complete", "Test"], ["register", "Iscritti"], ["trial_start", "Prove"]];
const PERIODS = [[7, "7 giorni"], [30, "30 giorni"], [90, "90 giorni"]];

function LinkBuilder() {
  const [name, setName] = useState("lancio");
  const url = `${window.location.origin}/lp?utm_source=instagram&utm_medium=ads&utm_campaign=${encodeURIComponent(name.trim().toLowerCase().replace(/\s+/g, "-") || "lancio")}`;
  const copy = () => navigator.clipboard.writeText(url).then(() => toast.success("Link copiato"));
  return (
    <div className="rounded-2xl border border-[#27272A] bg-[#121215] p-4">
      <p className="font-semibold text-[#FAF9F6]">Link per una campagna</p>
      <p className="mt-1 text-sm text-[#8A8E96]">Scrivi un nome (es. "lancio-giugno") e usa il link negli annunci: qui sotto vedrai i risultati per nome.</p>
      <input value={name} onChange={(e) => setName(e.target.value)} data-testid="campaign-name-input" className="mt-3 w-full rounded-xl border border-[#27272A] bg-[#09090B] px-3 py-2.5 text-sm text-[#FAF9F6]" />
      <div className="mt-2 flex items-center gap-2">
        <code className="flex-1 truncate rounded-lg bg-[#09090B] px-3 py-2 text-xs text-[#E5C158]" data-testid="campaign-link">{url}</code>
        <button onClick={copy} data-testid="campaign-copy-button" className="rounded-lg border border-[#27272A] p-2 text-[#D4AF37]"><Copy size={16} /></button>
      </div>
    </div>
  );
}

function PixelForm({ initial }) {
  const [id, setId] = useState(initial);
  const save = async () => {
    try {
      await api.put("/admin/site-config", { meta_pixel_id: id.trim() });
      toast.success(id.trim() ? "Meta Pixel salvato" : "Meta Pixel disattivato");
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail) || "L'ID deve contenere solo numeri");
    }
  };
  return (
    <div className="rounded-2xl border border-[#27272A] bg-[#121215] p-4">
      <p className="font-semibold text-[#FAF9F6]">Meta Pixel</p>
      <p className="mt-1 text-sm text-[#8A8E96]">Incolla l'ID numerico del tuo Pixel (Gestione eventi di Meta). Si attiva solo per chi accetta i cookie.</p>
      <div className="mt-3 flex gap-2">
        <input value={id} onChange={(e) => setId(e.target.value.replace(/\D/g, ""))} inputMode="numeric" placeholder="es. 1234567890123456" data-testid="pixel-id-input" className="min-w-0 flex-1 rounded-xl border border-[#27272A] bg-[#09090B] px-3 py-2.5 text-sm text-[#FAF9F6]" />
        <button onClick={save} data-testid="pixel-save-button" className="btn-gold flex items-center gap-1 rounded-xl px-4 text-sm font-semibold"><Save size={15} /> Salva</button>
      </div>
    </div>
  );
}

export default function AdminCampaigns() {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/admin/campaigns?days=${days}`).then(({ data }) => setData(data)).catch(() => navigate("/admin", { replace: true }));
  }, [days, navigate]);

  if (!data) return <Loader />;
  return (
    <AppShell title="Campagne" greeting="Area admin">
      <div className="space-y-5 px-5 pt-6 pb-10">
        <LinkBuilder />
        <div>
          <div className="mb-3 flex gap-2">
            {PERIODS.map(([d, l]) => (
              <button key={d} onClick={() => setDays(d)} data-testid={`campaign-period-${d}`} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${days === d ?"bg-[#D4AF37] text-[#09090B]" : "border border-[#27272A] text-[#B4B4BC]"}`}>{l}</button>
            ))}
          </div>
          <div className="overflow-x-auto rounded-2xl border border-[#27272A]" data-testid="campaign-table">
            <table className="w-full text-sm">
              <thead className="bg-[#121215] text-[#8A8E96]">
                <tr><th className="px-3 py-2 text-left font-medium">Campagna</th>{COLS.map(([k, l]) => <th key={k} className="px-2 py-2 text-right font-medium">{l}</th>)}</tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-[#8A8E96]">Nessuna visita nel periodo</td></tr>}
                {data.rows.map((r) => (
                  <tr key={`${r.campaign}-${r.source}`} className="border-t border-[#27272A] text-[#E4E4E7]">
                    <td className="px-3 py-2"><p className="font-semibold">{r.campaign}</p><p className="text-xs text-[#8A8E96]">{r.source}</p></td>
                    {COLS.map(([k]) => <td key={k} className="px-2 py-2 text-right tabular-nums">{r[k]}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-[#8A8E96]">Visite e clic contano la landing /lp; test, iscritti e prove contano tutte le persone arrivate da quella campagna.</p>
        </div>
        <PixelForm initial={data.meta_pixel_id} />
      </div>
    </AppShell>
  );
}
